<template>
  <div class="chart-card">
    <div class="card-top">
      <div class="summary-stats">
          <div v-for="(total, i) in totals" :key="i"
            class="stat-box"
            :class="{
              'can-be-toggled': total.canBeToggled,
              inactive: total.canBeToggled && !total.active
            }"
            @click="total.canBeToggled && $emit('toggleMetric', total.name)"
          >
            <span class="title">
              {{ total.value }}
            </span>
            <div class="category">
              <span class="category-color" :style="{ backgroundColor: colors[i] }"></span>
              <span class="category-text">{{ total.name }}</span>
            </div>
          </div>
      </div>
    </div>
    <div class="chart">
      <div class="chart-container">
        <apexchart
          class="chart"
          :options="chartOptions"
          :series="series"
          width="100%"
          height="100%"
          type="line"
          ref="chart"
        ></apexchart>
      </div>
    </div>
  </div>
</template>
  
<script>
import VueApexCharts from "vue-apexcharts";

const colors = ["#9A2CCA", "#A6CEE3", "#FFFF00", "#CC5500"];

export default {
  name: "Chart",
  components: {
    apexchart: VueApexCharts
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    metrics: {
      type: Object,
      default: () => ({})
    },
    summaryStats: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      colors: colors,
      totals: [],
      chartOptions: {
        grid: {
          yaxis: {
            lines: {
              show: true,
            },
          },
          padding: {
            top: 0,
            right: 10,
            bottom: 0,
            left: 10,
          },
        },
        markers: {
          size: 0,
          strokeWidth: 3,
          strokeColor: colors,
          colors: ["#24224A", "#24224A", "#24224A"],
          hover: {
            size: 5,
            strokeWidth: 4,
          },
        },
        tooltip: {
          theme: "dark",
          style: {
            fontSize: "14px",
            fontFamily: "Montserrat",
            colors: "#8786AB",
          },
        },
        chart: {
          dropShadow: {
            enabled: true,
            color: colors,
            opacity: 0.17,
          },
          background: "#24224A",
          id: "stats",
          height: 400,
          type: "line",
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        colors: colors,
        stroke: {
          curve: "smooth",
          colors: colors,
          width: 2,
        },
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        xaxis: {
          categories: [],
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            style: {
              fontSize: "14px",
              colors: "#8786AB",
              fontFamily: "Montserrat",
            },
          },
          type: "datetime",
          tooltip: {
            enabled: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "Montserrat",
              colors: "#8786AB",
            },
          },
        },
      },
      series: []
    };
  },
  watch: {
    data: {
      handler: "updateChart",
      deep: true,
    },
    metrics: {
      handler: "updateChart",
      deep: true,
    },
    summaryStats: {
      handler: "updateChart",
      deep: true,
    }
  },
  methods: {
    updateChart() {
      if (!Object.keys(this.data).length) return;
      if (!Object.keys(this.metrics).length) return;

      const activeMetrics = Object.keys(this.metrics).reduce((acc, curr) => {
        if (this.metrics[curr].active || !this.metrics[curr].canBeToggled) {
          acc[curr] = this.metrics[curr];
        }
        return acc;
      }, {});

      const dates = this.data[Object.keys(activeMetrics)[0]].map((data) => `${data.date}T00:00:00`);
      const colors = Object.keys(this.metrics).map((metric) => this.metrics[metric].color);
      this.colors = colors;

      this.$refs.chart.updateOptions({
        xaxis: { categories: dates },
        colors,
        markers: { strokeColor: colors },
        chart: { dropShadow: { color: colors } },
        stroke: { colors }
      });

      const metrics = Object.keys(this.data).flatMap((metric) => {
        if (!activeMetrics[metric]) return [];
        return [{
          name: metric,
          data: this.data[metric].map((data) => data.value || 0),
          color: activeMetrics[metric].color
        }];
      });

      this.$refs.chart.updateSeries(metrics);

      this.totals = Object.keys(this.data).map((metric) => {
        const data = this.data[metric].map((data) => data.value || 0);
        return {
          name: metric,
          value: metric === "Spotify Likes" ? data.pop() : data.reduce((acc, curr) => acc + curr, 0),
          color: this.metrics[metric].color,
          canBeToggled: this.metrics[metric].canBeToggled,
          active: this.metrics[metric].active
        };
      });

      if (this.summaryStats.length) {
        this.totals = this.totals.concat(this.summaryStats);
        this.colors = this.colors.concat(this.summaryStats.map((stat) => stat.color));
      }
    }
  },
};
</script>
<style>
.svg-no-label {
  top: 14px!important;
}
.apexcharts-gridline {
  stroke: rgba(177, 175, 205, 0.15);
}

.apexcharts-tooltip.apexcharts-active {
  background: #1b193c;
  border: none;
  border-radius: 5px;
  padding: 9px 14px;
}

.apexcharts-tooltip.apexcharts-theme-dark .apexcharts-tooltip-title {
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
}

.apexcharts-tooltip-text-label,
.apexcharts-tooltip-text-z-label {
  color: #6f6c99;
}

.apexcharts-tooltip-text-value,
.apexcharts-tooltip-text-z-value {
  color: white;
}
</style>
<style lang="scss" scoped>
.chart-card {
  margin-top: 18px;
  background: $bg-type-2;
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  padding: 24px;

  .card-top {
    display: flex;
    justify-content: space-between;

    .summary-stats {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      gap: 5px;
      .stat-box {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-width: 100px;
        min-height: 40px;
        margin-right: 24px;
        padding: 10px 10px;
        background: $purple-dark;
        border-radius: 10px;

        .title {
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          text-align: center;
          color: white;
        }

        .category {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;

          .category-color {
            width: 6px;
            height: 6px;
            border-radius: 50%;
          }

          .category-text {
            font-size: 12px;
            line-height: 15px;
            color: $text-type-1;
          }
        }

        &.can-be-toggled {
          cursor: pointer;
        }

        &.inactive {
          opacity: 0.5;
        }
      }
    }
  }

  .chart {
    width: 100%;
    height: 300px;
    margin-top: 36px;
  }
}

@media (max-width: 600px) {
  .chart-card {
    padding: 18px !important;
    .summary-stats {
      .stat-box {
        margin-right: 18px !important;
        min-width: 100px !important;
      }
    }
  }
}
</style>
